import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Building2, Plus, Pencil, Trash2, Target, ArrowUpRight,
  Loader2, X, ChevronDown, ChevronRight, Calendar, Percent, Euro,
} from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

// ---- shared inputs ----------------------------------------------------------
const inp =
  "w-full bg-card border border-border rounded-lg px-3.5 py-2 text-sm text-foreground focus:outline-none focus:border-primary";
const label = "text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-semibold block mb-1.5";

const METRIC_LABEL = {
  contract_value: "Valor do contrato (mensal em Continuidade, total em Pontual) €",
  deposit_total_paid: "Caução paga (€)",
  contracts_count: "Contratos fechados (nº)",
  monthly_value_annualised: "Valor do contrato (legado — mesma regra que «Valor do contrato»)",
};

const STATUS_LABEL = {
  active: "Activa",
  closed: "Fechada",
};

function fmtEUR(v) {
  return new Intl.NumberFormat("pt-PT", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(Number(v || 0));
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("pt-PT");
}

// ---- OFFICE FORM DIALOG -----------------------------------------------------
function OfficeDialog({ open, onOpenChange, editing, onSaved }) {
  const [form, setForm] = useState({ name: "", code: "", notes: "", active: true });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        name: editing?.name || "",
        code: editing?.code || "",
        notes: editing?.notes || "",
        active: editing?.active ?? true,
      });
    }
  }, [open, editing]);

  const save = async () => {
    const name = form.name.trim();
    if (!name) { toast.error("Nome obrigatório"); return; }
    setSaving(true);
    try {
      if (editing?.id) {
        await api.patch(`/offices/${editing.id}`, {
          name,
          code: form.code.trim() || null,
          notes: form.notes || null,
          active: form.active,
        });
        toast.success("Escritório actualizado");
      } else {
        await api.post("/offices", {
          name,
          code: form.code.trim() || null,
          notes: form.notes || null,
        });
        toast.success("Escritório criado");
      }
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao gravar");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="office-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {editing?.id ? "Editar escritório" : "Novo escritório"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Formulário para {editing?.id ? "editar" : "criar"} um escritório operacional.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className={label}>Nome *</label>
            <input
              data-testid="office-name-input"
              className={inp}
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Porto, Lisboa, Braga…"
              autoFocus
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              Deve coincidir com o valor guardado em <code className="bg-muted/40 px-1 rounded">lead.office</code>.
            </p>
          </div>
          <div>
            <label className={label}>Código curto</label>
            <input
              data-testid="office-code-input"
              className={inp}
              value={form.code}
              onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
              placeholder="PT, LX, BR (opcional)"
              maxLength={8}
            />
          </div>
          <div>
            <label className={label}>Notas</label>
            <textarea
              data-testid="office-notes-input"
              className={inp + " min-h-[80px]"}
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Contexto interno (opcional)"
            />
          </div>
          {editing?.id && (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.active}
                onChange={e => setForm(f => ({ ...f, active: e.target.checked }))}
                data-testid="office-active-toggle"
              />
              Escritório activo
            </label>
          )}
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <button
            onClick={() => onOpenChange(false)}
            className="px-4 py-2 rounded-lg text-sm text-muted-foreground hover:bg-muted/40"
          >Cancelar</button>
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-foreground text-background text-sm font-medium hover:bg-foreground/90 disabled:opacity-50"
            data-testid="office-save-btn"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {editing?.id ? "Guardar" : "Criar escritório"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---- OBJECTIVE FORM DIALOG --------------------------------------------------
function ObjectiveDialog({ open, onOpenChange, office, editing, onSaved }) {
  const initial = {
    name: "",
    start_date: "",
    end_date: "",
    target_metric: "contract_value",
    target_value: "",
    bonus_percentage: "",
    notes: "",
    status: "active",
  };
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(editing?.id ? {
        name: editing.name,
        start_date: editing.start_date,
        end_date: editing.end_date,
        target_metric: editing.target_metric,
        target_value: String(editing.target_value),
        bonus_percentage: String(editing.bonus_percentage),
        notes: editing.notes || "",
        status: editing.status || "active",
      } : initial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing]);

  const save = async () => {
    if (!form.name.trim() || !form.start_date || !form.end_date) {
      toast.error("Nome, data início e fim são obrigatórios");
      return;
    }
    const target = parseFloat(form.target_value);
    const bonus = parseFloat(form.bonus_percentage);
    if (!target || target <= 0) { toast.error("Target deve ser > 0"); return; }
    if (isNaN(bonus) || bonus < 0 || bonus > 100) { toast.error("Bónus % entre 0 e 100"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        start_date: form.start_date,
        end_date: form.end_date,
        target_metric: form.target_metric,
        target_value: target,
        bonus_percentage: bonus,
        notes: form.notes || null,
      };
      if (editing?.id) {
        await api.patch(`/objectives/${editing.id}`, { ...payload, status: form.status });
        toast.success("Objectivo actualizado");
      } else {
        await api.post(`/offices/${office.id}/objectives`, payload);
        toast.success("Objectivo criado");
      }
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao gravar");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="objective-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {editing?.id ? "Editar objectivo" : `Novo objectivo · ${office?.name || ""}`}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Formulário para {editing?.id ? "editar" : "criar"} um objectivo do escritório com período,
            métrica alvo, valor de target e percentagem de bónus.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className={label}>Nome *</label>
            <input
              data-testid="obj-name-input"
              className={inp}
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Q1 2026, Fevereiro 2026…"
              autoFocus
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={label}>Início *</label>
              <input
                type="date"
                data-testid="obj-start-input"
                className={inp}
                value={form.start_date}
                onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
              />
            </div>
            <div>
              <label className={label}>Fim *</label>
              <input
                type="date"
                data-testid="obj-end-input"
                className={inp}
                value={form.end_date}
                onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
              />
            </div>
          </div>

          <div>
            <label className={label}>Métrica *</label>
            <select
              data-testid="obj-metric-select"
              className={inp}
              value={form.target_metric}
              onChange={e => setForm(f => ({ ...f, target_metric: e.target.value }))}
            >
              {Object.entries(METRIC_LABEL).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            <p className="text-[11px] text-muted-foreground mt-1">
              {form.target_metric === "contract_value" && "Média mensal do contrato para Continuidade; valor total para Pontual. Reflecte o que o cliente vai pagar."}
              {form.target_metric === "deposit_total_paid" && "Soma dos pagamentos de caução recebidos no período."}
              {form.target_metric === "contracts_count" && "Nº de leads convertidos (contract validated + caução paga) no período."}
              {form.target_metric === "monthly_value_annualised" && "⚠ Métrica legada — hoje calcula exactamente como «Valor do contrato». Preferir a nova."}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={label}>Target *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                data-testid="obj-target-input"
                className={inp}
                value={form.target_value}
                onChange={e => setForm(f => ({ ...f, target_value: e.target.value }))}
                placeholder={form.target_metric === "contracts_count" ? "10" : "50000"}
              />
            </div>
            <div>
              <label className={label}>Bónus % *</label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="100"
                data-testid="obj-bonus-input"
                className={inp}
                value={form.bonus_percentage}
                onChange={e => setForm(f => ({ ...f, bonus_percentage: e.target.value }))}
                placeholder="2.5"
              />
            </div>
          </div>

          {editing?.id && (
            <div>
              <label className={label}>Estado</label>
              <select
                data-testid="obj-status-select"
                className={inp}
                value={form.status}
                onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
              >
                <option value="active">Activa</option>
                <option value="closed">Fechada</option>
              </select>
            </div>
          )}

          <div>
            <label className={label}>Notas</label>
            <textarea
              data-testid="obj-notes-input"
              className={inp + " min-h-[70px]"}
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <button
            onClick={() => onOpenChange(false)}
            className="px-4 py-2 rounded-lg text-sm text-muted-foreground hover:bg-muted/40"
          >Cancelar</button>
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-foreground text-background text-sm font-medium hover:bg-foreground/90 disabled:opacity-50"
            data-testid="obj-save-btn"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {editing?.id ? "Guardar" : "Criar objectivo"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---- OFFICE ROW WITH EXPANDABLE OBJECTIVES ---------------------------------
function OfficeRow({ office, onEdit, onDelete, onOpenObjective, canWrite, registerReload }) {
  const [expanded, setExpanded] = useState(false);
  const [objectives, setObjectives] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/offices/${office.id}/objectives`);
      setObjectives(r.data);
    } catch (e) {
      toast.error("Erro a carregar objectivos");
    } finally { setLoading(false); }
  };

  // Register a reload callback with the parent so it can refresh
  // objectives WITHOUT forcing a remount (which would collapse the row).
  useEffect(() => {
    if (registerReload) registerReload(office.id, load);
    return () => { if (registerReload) registerReload(office.id, null); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [office.id]);

  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && objectives === null) load();
  };

  const removeObj = async (obj) => {
    if (!window.confirm(`Apagar objectivo "${obj.name}"?`)) return;
    try {
      await api.delete(`/objectives/${obj.id}`);
      toast.success("Objectivo removido");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  return (
    <div className="border border-border rounded-xl bg-card" data-testid={`office-row-${office.id}`}>
      <div className="flex items-center gap-4 px-5 py-4">
        <button
          onClick={toggle}
          className="text-muted-foreground hover:text-foreground"
          data-testid={`office-toggle-${office.id}`}
          aria-label={expanded ? "Fechar" : "Abrir"}
        >
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>

        <div className="w-10 h-10 rounded-lg bg-background flex items-center justify-center flex-shrink-0">
          <Building2 className="w-5 h-5 text-foreground" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <span className="font-display text-xl truncate" data-testid={`office-name-${office.id}`}>{office.name}</span>
            {office.code && (
              <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full bg-muted/40 text-muted-foreground font-semibold">
                {office.code}
              </span>
            )}
            {!office.active && (
              <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full bg-[hsl(var(--danger-bg))] text-[hsl(var(--danger))] font-semibold">
                Inactivo
              </span>
            )}
          </div>
          {office.notes && <div className="text-xs text-muted-foreground mt-0.5 truncate">{office.notes}</div>}
        </div>

        {canWrite && (
          <div className="flex items-center gap-1">
            <button
              onClick={() => onEdit(office)}
              className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40"
              title="Editar"
              data-testid={`office-edit-${office.id}`}
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button
              onClick={() => onDelete(office)}
              className="p-2 rounded-md text-muted-foreground hover:text-[hsl(var(--danger))] hover:bg-[hsl(var(--danger-bg))]"
              title="Apagar"
              data-testid={`office-delete-${office.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {expanded && (
        <div className="border-t border-border px-5 py-4 bg-muted/40" data-testid={`office-objectives-${office.id}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">
              Objectivos ({objectives?.length || 0})
            </div>
            {canWrite && (
              <button
                onClick={() => onOpenObjective(office, null)}
                className="inline-flex items-center gap-1.5 text-xs text-foreground hover:underline font-semibold"
                data-testid={`obj-new-${office.id}`}
              >
                <Plus className="w-3.5 h-3.5" /> Novo objectivo
              </button>
            )}
          </div>

          {loading && <div className="text-xs text-muted-foreground py-3">A carregar…</div>}

          {!loading && objectives?.length === 0 && (
            <div className="text-sm text-muted-foreground py-4 text-center bg-card rounded-lg border border-dashed border-border">
              Nenhum objectivo definido — {canWrite ? "clica em «Novo objectivo» para criar o primeiro." : "peça a um admin para adicionar."}
            </div>
          )}

          {!loading && objectives?.length > 0 && (
            <div className="space-y-2">
              {objectives.map(obj => (
                <div key={obj.id} className="flex items-center gap-3 bg-card rounded-lg border border-border p-3" data-testid={`obj-row-${obj.id}`}>
                  <Target className="w-4 h-4 text-foreground flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold truncate">{obj.name}</span>
                      <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full font-semibold ${
                        obj.status === "active" ? "bg-[hsl(var(--success-bg))] text-[hsl(var(--success))]" : "bg-muted/40 text-muted-foreground"
                      }`}>{STATUS_LABEL[obj.status] || obj.status}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {fmtDate(obj.start_date)} → {fmtDate(obj.end_date)}</span>
                      <span className="inline-flex items-center gap-1"><Euro className="w-3 h-3" /> {METRIC_LABEL[obj.target_metric]}: {obj.target_metric === "contracts_count" ? `${obj.target_value}` : fmtEUR(obj.target_value)}</span>
                      <span className="inline-flex items-center gap-1"><Percent className="w-3 h-3" /> {obj.bonus_percentage}%</span>
                    </div>
                  </div>

                  <Link
                    to={`/objectives/${obj.id}`}
                    className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md bg-foreground text-background hover:bg-foreground/90 font-medium"
                    data-testid={`obj-view-${obj.id}`}
                  >
                    Dashboard <ArrowUpRight className="w-3 h-3" />
                  </Link>

                  {canWrite && (
                    <>
                      <button
                        onClick={() => onOpenObjective(office, obj)}
                        className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40"
                        title="Editar"
                        data-testid={`obj-edit-${obj.id}`}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => removeObj(obj)}
                        className="p-1.5 rounded-md text-muted-foreground hover:text-[hsl(var(--danger))] hover:bg-[hsl(var(--danger-bg))]"
                        title="Apagar"
                        data-testid={`obj-delete-${obj.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---- PAGE -------------------------------------------------------------------
export default function OfficesSettings() {
  const [offices, setOffices] = useState(null);
  const [loading, setLoading] = useState(false);
  const [officeDialog, setOfficeDialog] = useState({ open: false, editing: null });
  const [objDialog, setObjDialog] = useState({ open: false, office: null, editing: null });

  // Registry of reload callbacks (office_id → fn) so we can trigger a
  // targeted reload of a single OfficeRow without remounting it and losing
  // its expanded state.
  const reloadFnsRef = useState(() => new Map())[0];

  const loadOffices = async () => {
    setLoading(true);
    try {
      const r = await api.get("/offices");
      setOffices(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao carregar escritórios");
      setOffices([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadOffices(); }, []);

  const user = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("caring_user") || "null"); }
    catch { return null; }
  }, []);
  const canWrite = ["admin", "direcao", "dgo"].includes(user?.role);

  const removeOffice = async (office) => {
    if (!window.confirm(`Apagar escritório "${office.name}"? Só é possível se não tiver objectivos.`)) return;
    try {
      await api.delete(`/offices/${office.id}`);
      toast.success("Escritório apagado");
      loadOffices();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const registerReload = (officeId, fn) => {
    if (fn) reloadFnsRef.set(officeId, fn);
    else reloadFnsRef.delete(officeId);
  };

  const reloadObjectivesFor = (officeId) => {
    const fn = reloadFnsRef.get(officeId);
    if (fn) fn();
  };

  return (
    <div className="p-8 lg:p-12 max-w-5xl mx-auto space-y-6" data-testid="offices-root">
      <div className="flex items-start justify-between gap-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-2">Organização</div>
          <h1 className="font-display text-4xl lg:text-5xl tracking-tight">Escritórios & Objectivos</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Define escritórios operacionais (Porto, Lisboa, Braga…) e metas de faturação por período.
            Os operadores de atendimento recebem uma percentagem sobre o valor que trouxeram
            quando o escritório atinge a meta.
          </p>
        </div>
        {canWrite && (
          <button
            onClick={() => setOfficeDialog({ open: true, editing: null })}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-foreground text-background text-sm font-medium hover:bg-foreground/90 flex-shrink-0"
            data-testid="office-new-btn"
          >
            <Plus className="w-4 h-4" /> Novo escritório
          </button>
        )}
      </div>

      {loading && (
        <div className="text-sm text-muted-foreground flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> A carregar escritórios…
        </div>
      )}

      {!loading && offices?.length === 0 && (
        <div className="border-2 border-dashed border-border rounded-2xl p-12 text-center bg-card" data-testid="offices-empty">
          <Building2 className="w-12 h-12 mx-auto text-foreground mb-3" />
          <div className="font-display text-2xl mb-2">Ainda não há escritórios</div>
          <p className="text-sm text-muted-foreground max-w-md mx-auto mb-4">
            Cria o primeiro escritório para começares a definir objectivos e calcular bónus.
          </p>
          {canWrite && (
            <button
              onClick={() => setOfficeDialog({ open: true, editing: null })}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-foreground text-background text-sm font-medium hover:bg-foreground/90"
              data-testid="office-new-empty-btn"
            >
              <Plus className="w-4 h-4" /> Criar primeiro escritório
            </button>
          )}
        </div>
      )}

      {!loading && offices?.length > 0 && (
        <div className="space-y-3" data-testid="offices-list">
          {offices.map(o => (
            <OfficeRow
              key={o.id}
              office={o}
              canWrite={canWrite}
              onEdit={(off) => setOfficeDialog({ open: true, editing: off })}
              onDelete={removeOffice}
              onOpenObjective={(off, obj) => setObjDialog({ open: true, office: off, editing: obj })}
              registerReload={registerReload}
            />
          ))}
        </div>
      )}

      <OfficeDialog
        open={officeDialog.open}
        onOpenChange={(o) => setOfficeDialog(s => ({ ...s, open: o }))}
        editing={officeDialog.editing}
        onSaved={loadOffices}
      />

      <ObjectiveDialog
        open={objDialog.open}
        onOpenChange={(o) => setObjDialog(s => ({ ...s, open: o }))}
        office={objDialog.office}
        editing={objDialog.editing}
        onSaved={() => {
          if (objDialog.office?.id) reloadObjectivesFor(objDialog.office.id);
        }}
      />
    </div>
  );
}
